import React, { useState, useEffect } from 'react';
import { Spinner } from 'react-bootstrap';

const PowerPointPreview = ({ filePath }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewerType, setViewerType] = useState('office'); // 'office', 'google', or 'fallback'

  // Encode the file path for the URL
  const encodedPath = encodeURIComponent(filePath);

  // Different viewer options
  const officeOnlineUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodedPath}`;
  const googleDocsUrl = `https://docs.google.com/gview?url=${encodedPath}&embedded=true`;

  useEffect(() => {
    // Reset state when filePath changes
    setLoading(true);
    setError(null);
    setViewerType('office');
  }, [filePath]);

  const handleOfficeViewerLoad = () => {
    setLoading(false);
  };

  const handleOfficeViewerError = () => {
    console.log('Office viewer failed, switching to Google Docs viewer');
    setViewerType('google');
    // Keep loading true as we're switching to Google viewer
  };

  const handleGoogleViewerLoad = () => {
    setLoading(false);
  };

  const handleGoogleViewerError = () => {
    console.log('Google Docs viewer failed, showing download option');
    setError('Failed to load preview. Please download the file to view it.');
    setViewerType('fallback');
    setLoading(false);
  };

  return (
    <div className="powerpoint-preview" style={{ position: 'relative', width: '100%', height: '70vh' }}>
      {loading && (
        <div className="loading-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: 'rgba(255,255,255,0.8)',
          zIndex: 10
        }}>
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Loading presentation...</div>
          </div>
        </div>
      )}

      {viewerType === 'office' && (
        <iframe
          src={officeOnlineUrl}
          onLoad={handleOfficeViewerLoad}
          onError={handleOfficeViewerError}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="PowerPoint Preview - Office"
          allowFullScreen
        />
      )}

      {viewerType === 'google' && (
        <iframe
          src={googleDocsUrl}
          onLoad={handleGoogleViewerLoad}
          onError={handleGoogleViewerError}
          style={{ width: '100%', height: '100%', border: 'none' }}
          title="PowerPoint Preview - Google"
          allowFullScreen
        />
      )}

      {viewerType === 'fallback' && (
        <div className="fallback-view" style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div className="error-icon mb-3">
            <i className="bi bi-file-earmark-ppt" style={{ fontSize: '48px', color: '#d24726' }}></i>
          </div>
          <h4 className="mb-3">PowerPoint Preview Unavailable</h4>
          <p className="mb-4">{error || 'Unable to display a preview for this PowerPoint file.'}</p>
          <a
            href={filePath}
            download
            className="btn btn-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download Presentation
          </a>
        </div>
      )}

      <div className="preview-footer" style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        textAlign: 'center',
        padding: '8px',
        borderTop: '1px solid #eee',
        backgroundColor: '#f8f9fa'
      }}>
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {viewerType === 'office' ? 'Using Office Online Viewer' :
              viewerType === 'google' ? 'Using Google Docs Viewer' : 'Preview not available'}
          </small>
          <a
            href={filePath}
            className="btn btn-sm btn-outline-secondary"
            target="_blank"
            rel="noopener noreferrer"
          >
            <i className="bi bi-download me-1"></i> Download
          </a>
        </div>
      </div>
    </div>
  );
};

export default PowerPointPreview; 